import { Alert, Button, Divider, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { ContentSection } from 'src/shared/components/organisms/contentSection';

export type AutogiroReminderSettings = {
  autogiroRemindersEnabled: boolean;
  autogiroReminder1Subject: string;
  autogiroReminder1Body: string;
  autogiroReminder2Subject: string;
  autogiroReminder2Body: string;
};

type AutogiroRemindersSettingsProps = {
  initialValues: Partial<AutogiroReminderSettings>;
  // Platshållare som kan användas i ämne och text för påminnelserna.
  customFields: { label: string; value: string }[];
  // Översättningsprefix, t.ex. 'fundraiser:autogiroReminders'. Nycklarna under prefixet:
  // heading, enable, subject, availableFields, reminder1, reminder1Description, reminder2, reminder2Description.
  translationPrefix: string;
  onSave: (settings: AutogiroReminderSettings) => Promise<unknown>;
};

type AutogiroRemindersForm = {
  reminder1Subject: string;
  reminder1Body: string;
  reminder2Subject: string;
  reminder2Body: string;
};

/**
 * Inställningar för autogiro-påminnelser (e-post) som skickas när ett autogiromedgivande inte är signerat.
 * Används både för insamlingar (månadsgivare) och fakturagrupper (medlemsavgift).
 */
export const AutogiroRemindersSettings = (props: AutogiroRemindersSettingsProps) => {
  const { initialValues, customFields, translationPrefix, onSave } = props;
  const { t } = useTranslation(['fundraiser', 'invoice', 'common']);
  const tk = (key: string) => t(`${translationPrefix}.${key}`);

  const [remindersEnabled, setRemindersEnabled] = useState(initialValues.autogiroRemindersEnabled ?? false);

  const formMethods = useForm<AutogiroRemindersForm>({
    mode: 'all',
    defaultValues: {
      reminder1Subject: initialValues.autogiroReminder1Subject ?? '',
      reminder1Body: initialValues.autogiroReminder1Body ?? '',
      reminder2Subject: initialValues.autogiroReminder2Subject ?? '',
      reminder2Body: initialValues.autogiroReminder2Body ?? '',
    },
  });

  const { handleSubmit } = formMethods;

  const onSubmit = async (data: AutogiroRemindersForm) => {
    try {
      await onSave({
        autogiroRemindersEnabled: remindersEnabled,
        autogiroReminder1Subject: data.reminder1Subject,
        autogiroReminder1Body: data.reminder1Body,
        autogiroReminder2Subject: data.reminder2Subject,
        autogiroReminder2Body: data.reminder2Body,
      });
      toast.success(t('common:success.save') as string);
    } catch {
      toast.error(t('common:error.save') as string);
    }
  };

  const renderReminder = (step: 1 | 2) => (
    <Grid item xs={12}>
      <ContentSection>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant='subtitle1' fontWeight='bold'>
              {tk(`reminder${step}`)}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {tk(`reminder${step}Description`)}
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <ControlledTextField name={`reminder${step}Subject`} label={tk('subject')} size='small' fullWidth sx={{ mt: 1, mb: 2 }} />
            <ControlledTextEditor name={`reminder${step}Body`} rows={30} noMargin customFields={customFields} />
          </Grid>
        </Grid>
      </ContentSection>
    </Grid>
  );

  return (
    <Grid item xs={12}>
      <Typography variant='h6' sx={{ fontWeight: 600, mt: 2 }} gutterBottom>
        {tk('heading')}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={remindersEnabled} onChange={(e) => setRemindersEnabled(e.target.checked)} />}
                label={tk('enable')}
              />
            </Grid>

            {remindersEnabled && (
              <>
                <Grid item xs={12}>
                  <Alert severity='info'>{tk('availableFields')}</Alert>
                </Grid>

                {renderReminder(1)}

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {renderReminder(2)}
              </>
            )}

            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant='contained' disableElevation type='submit'>
                {t('common:button.save')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Grid>
  );
};
